/*
 *** Usage in FO: OK
 *** Usage in PO: OK
 *** Usage in BO: OK
 */

import PropTypes from 'prop-types';
import styled, { useTheme, css } from 'styled-components';

import { Box } from '@hivebrite/design-system/Box';
import { Dotdotdot } from '@hivebrite/design-system/Dotdotdot';
import { FakeLink } from '@hivebrite/design-system/Link';
import { useBreakpoint } from '@hivebrite/design-system/Theme';

import SuperCategoryLogo from 'hb-react/shared/icons/SuperCategoryLogo';
import onEnterOrSpacePress from 'hb-react/shared/utils/onEnterOrSpacePress';

const logoBoxSize = { _: 101, m: 124, l: 78, xl: 148 };

const DefaultLogo = () => {
  const theme = useTheme();
  const breakpoint = useBreakpoint();

  const iconSize = { s: 48, m: 60, l: 48, xl: 60 };
  const iconWidth = iconSize[breakpoint];

  return (
    <Box
      bg={theme.colors.primary}
      minWidth={logoBoxSize}
      height={logoBoxSize}
      css={css`
        border-radius: 8px;
      `}
      alignItems="center"
      justifyContent="center"
      data-testid="default-logo"
    >
      <SuperCategoryLogo fillColor={theme.colors.primary} size={iconWidth} />
    </Box>
  );
};

const BoxLogo = ({ picture, seeGroupsAction }) => (
  <Box
    minWidth={logoBoxSize}
    onClick={seeGroupsAction}
    style={{ cursor: 'pointer' }}
  >
    {picture ? (
      <Box width={logoBoxSize} height={logoBoxSize}>
        <ImageWrapper backgroundImage={picture} />
      </Box>
    ) : (
      <DefaultLogo />
    )}
  </Box>
);

const SuperCategoryCard = ({
  description,
  name,
  picture,
  seeGroupsAction,
  seeGroupsLabel,
  seeGroupsA11yLabel,
}) => (
  <Box flexDirection="row" data-testid="super-category-card">
    <BoxLogo picture={picture} seeGroupsAction={seeGroupsAction} />
    <Box flexDirection="column" ml={5}>
      <Box
        as="h3"
        font="h3-semibold"
        onClick={seeGroupsAction}
        style={{ cursor: 'pointer' }}
      >
        {name}
      </Box>
      {description && (
        <Box mt="6px">
          <Dotdotdot clamp={3}>{description}</Dotdotdot>
        </Box>
      )}
      <Box mt={3}>
        <FakeLink
          as="span"
          display="inline"
          onClick={seeGroupsAction}
          onKeyDown={onEnterOrSpacePress(seeGroupsAction)}
          aria-label={seeGroupsA11yLabel}
        >
          {seeGroupsLabel}
        </FakeLink>
      </Box>
    </Box>
  </Box>
);

SuperCategoryCard.propTypes = {
  description: PropTypes.string,
  name: PropTypes.string.isRequired,
  picture: PropTypes.string,
  seeGroupsAction: PropTypes.func.isRequired,
  seeGroupsLabel: PropTypes.string.isRequired,
  seeGroupsA11yLabel: PropTypes.string.isRequired,
};

export default SuperCategoryCard;

const ImageWrapper = styled(Box).withConfig({
  shouldForwardProp: (prop) => !['backgroundImage'].includes(prop),
})`
  background-image: ${({ backgroundImage }) => `url(${backgroundImage})`};
  background-position: center;
  background-repeat: no-repeat;
  background-size: cover;
  height: 100%;
  width: 100%;
  border-radius: 8px;
`;
